import { useExchangeRates } from '../../hooks/useExchangeRates';
import { formatCurrency } from '../../utils/format/currency';
import { TrendingUp, TrendingDown } from 'lucide-react';

export function MarketTicker() {
  const { data: rates, isLoading } = useExchangeRates();

  const nairaItems = (rates ?? []).map(rate => ({
    label: `${rate.fromCurrency}/NGN`,
    value: formatCurrency(rate.rate),
    change: rate.change,
  }));

  const baseRates = Object.fromEntries(
    (rates ?? []).map(rate => [rate.fromCurrency, rate.rate]),
  );
  const usdRate = baseRates.USD ?? 1368;
  const crossPairs = [
    ['EUR/USD', 'EUR', 'USD'],
    ['GBP/USD', 'GBP', 'USD'],
    ['USD/JPY', 'USD', 'JPY'],
    ['USD/CHF', 'USD', 'CHF'],
    ['AUD/USD', 'AUD', 'USD'],
    ['USD/CAD', 'USD', 'CAD'],
  ].flatMap(([label, from, to]) => {
    const fromRate = baseRates[from];
    const toRate = baseRates[to];
    if (!fromRate || !toRate) return [];
    const value = from === 'USD' && to === 'JPY'
      ? 150
      : from === 'USD' && to === 'CHF'
        ? 0.88
        : fromRate / toRate;
    return [{ label, value: value.toFixed(4), change: 0.12 }];
  });

  const fallbackItems = [
    { label: 'USD/NGN', value: '₦1,368.00', change: 0.52 },
    { label: 'EUR/NGN', value: '₦1,569.00', change: -0.31 },
    { label: 'GBP/NGN', value: '₦1,900.00', change: 0.18 },
    { label: 'CAD/NGN', value: '₦1,020.00', change: -0.12 },
    { label: 'AUD/NGN', value: '₦970.00', change: 0.27 },
    { label: 'XOF/NGN', value: '₦2.39', change: 0.09 },
    { label: 'EUR/USD', value: '1.0930', change: 0.14 },
    { label: 'GBP/USD', value: '1.2760', change: -0.08 },
    { label: 'USD/JPY', value: '150.0000', change: 0.22 },
    { label: 'USD/CHF', value: '0.8800', change: -0.11 },
    { label: 'AUD/USD', value: '0.6610', change: 0.17 },
    { label: 'USD/CAD', value: '1.3610', change: -0.06 },
    { label: 'Brent', value: '$86.40', change: 0.88 },
  ];

  const items = nairaItems.length > 0 ? [...nairaItems, ...crossPairs] : [];

  const displayItems = isLoading || items.length === 0 ? fallbackItems : items;
  const doubled = [...displayItems, ...displayItems];

  return (
    <div className="bg-white border-b border-gray-200 overflow-hidden relative z-40">
      <div className="flex items-center h-7 sm:h-8">
        <div className="flex-shrink-0 bg-[#075b49] h-full flex items-center px-2 sm:px-2.5 z-10">
          <span className="text-[9px] font-bold text-white uppercase tracking-wider whitespace-nowrap flex items-center gap-1">
            <span className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse" />
            LIVE
          </span>
        </div>
        <div className="overflow-hidden flex-1 relative">
          <div className="flex items-center gap-4 sm:gap-5 animate-marquee whitespace-nowrap">
            {doubled.map((item, idx) => (
              <div key={idx} className="flex items-center gap-1.5 text-[10px] sm:text-[11px]">
                <span className="font-semibold text-gray-700">{item.label}</span>
                <span className="font-bold text-gray-900">{item.value}</span>
                <span className={`flex items-center gap-0.5 font-medium ${
                  item.change >= 0 ? 'text-emerald-600' : 'text-red-500'
                }`}>
                  {item.change >= 0
                    ? <TrendingUp className="h-2.5 w-2.5" />
                    : <TrendingDown className="h-2.5 w-2.5" />}
                  {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
                </span>
                <span className="text-gray-300">•</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
